import {
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { Router } from "@angular/router";
import { FlowTestsService } from "../flow-tests.service";
import { resolveTriggerForm } from "../trigger-forms/trigger-form.registry";
import { DefaultTriggerFormComponent } from "../trigger-forms/default-trigger-form.component";

interface FlowOption {
  id: number;
  name: string;
  entry_event_type_id: number;
  entry_event_type_name?: string;
  disabled?: boolean;
}

@Component({
  selector: "app-flow-test-create",
  templateUrl: "./flow-test-create.component.html",
  styleUrls: ["./flow-test-create.component.scss"],
})
export class FlowTestCreateComponent implements OnInit {
  flows: FlowOption[] = [];
  entryEventTypes: Array<{ id: number; name: string }> = [];
  selectedFlow: FlowOption | null = null;
  isLoadingFlows = false;
  isSubmitting = false;
  config: Record<string, any> = {};
  errorMessage: string | null = null;

  // static: false — the host lives inside *ngIf="selectedFlow" so Angular
  // only materializes it after change detection runs following a flow
  // selection. mountTriggerForm() defers via setTimeout so the query has
  // resolved before we read it.
  @ViewChild("triggerHost", { read: ViewContainerRef, static: false })
  triggerHost!: ViewContainerRef;

  private triggerRef: ComponentRef<any> | null = null;

  constructor(
    private flowTests: FlowTestsService,
    private router: Router,
    private resolver: ComponentFactoryResolver,
  ) {}

  ngOnInit(): void {
    this.isLoadingFlows = true;
    // Load entry event types first so we can name-map against flows.
    this.flowTests.getEntryEventTypes().subscribe({
      next: (res: any) => {
        const records = Array.isArray(res) ? res : res?.records || [];
        this.entryEventTypes = records.map((r: any) => ({ id: r.id, name: r.name }));
        this.loadFlows();
      },
      error: () => {
        this.entryEventTypes = [];
        this.loadFlows();
      },
    });
  }

  private loadFlows(): void {
    this.flowTests.listFlows().subscribe({
      next: (res: any) => {
        const records = Array.isArray(res) ? res : res?.records || [];
        this.flows = records.map((lt: any) => ({
          id: lt.id,
          name: lt.name,
          entry_event_type_id: lt.entry_event_type_id,
          entry_event_type_name: this.lookupEntryEventTypeName(lt.entry_event_type_id),
          disabled: !!lt.disabled,
        }));
        this.isLoadingFlows = false;
      },
      error: () => {
        this.flows = [];
        this.isLoadingFlows = false;
      },
    });
  }

  private lookupEntryEventTypeName(id: number): string | undefined {
    return this.entryEventTypes.find((t) => t.id === id)?.name;
  }

  onFlowChange(flow: FlowOption | null): void {
    this.selectedFlow = flow;
    this.config = {};
    // Destroy any existing trigger form synchronously so its subscription
    // doesn't keep pushing into `this.config` while we wait for the view
    // to re-materialize.
    if (this.triggerRef) {
      this.triggerRef.destroy();
      this.triggerRef = null;
    }
    // Defer until after change detection has inserted the *ngIf content
    // that contains <ng-container #triggerHost>.
    setTimeout(() => this.mountTriggerForm(), 0);
  }

  private mountTriggerForm(): void {
    if (!this.selectedFlow) return;
    if (!this.triggerHost) return;

    const entryEventType = this.selectedFlow.entry_event_type_name || null;
    const component: Type<any> = resolveTriggerForm(entryEventType);
    const factory = this.resolver.resolveComponentFactory(component);
    this.triggerRef = this.triggerHost.createComponent(factory);
    const instance = this.triggerRef.instance as DefaultTriggerFormComponent;
    instance.entryEventType = entryEventType;
    if (instance.configChange && typeof instance.configChange.subscribe === "function") {
      instance.configChange.subscribe((cfg: Record<string, any>) => {
        this.config = cfg || {};
      });
    }
  }

  cancel(): void {
    this.router.navigate(["/flow-tests", "list"]);
  }

  submit(): void {
    if (!this.selectedFlow || this.isSubmitting) return;
    this.isSubmitting = true;
    this.errorMessage = null;
    this.flowTests.create(this.selectedFlow.id, this.config).subscribe({
      next: (test) => {
        this.isSubmitting = false;
        this.router.navigate(["/flow-tests", test.id]);
      },
      error: (err) => {
        this.isSubmitting = false;
        this.errorMessage = err?.error?.message || err?.message || "Failed to queue test";
      },
    });
  }
}
